import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import type { AppRouter, RouterQuery, RouterUrlObject } from './types';
import { mergeRouterQuery } from './mergeRouterQuery';
import {
  addTrailingSlashToHref,
  addTrailingSlashToPathname,
  unblockRoutingChange,
} from '../../shared/routerUtils';

const stripTrailingSlash = (pathname: string): string =>
  pathname.length > 1 ? pathname.replace(/\/+$/u, '') : pathname;

const buildSearch = (query: RouterQuery | undefined): string => {
  if (!query) {
    return '';
  }

  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined) {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((item) => searchParams.append(key, item));
    } else {
      searchParams.append(key, value);
    }
  }

  const search = searchParams.toString();
  return search ? `?${search}` : '';
};

/**
 * Normalizes a push/replace argument into a React Router navigate target,
 * re-applying the env-appropriate trailing slash so client-side navigation
 * matches the URL shape the server serves on a full page load.
 */
const toNavigateTarget = (
  url: string | RouterUrlObject,
): string | { pathname: string; search: string } =>
  typeof url === 'string'
    ? addTrailingSlashToHref(url)
    : {
        pathname: addTrailingSlashToPathname(url.pathname),
        search: buildSearch(url.query),
      };

/**
 * Resolves a push/replace target to a cross-origin href that must be reached
 * via a full-page navigation, or `null` when the target is internal.
 *
 * React Router's `navigate` resolves absolute URLs as in-app paths (e.g.
 * `https://checkout.stripe.com/...` becomes `/v2/.../https:/checkout...`), so
 * cross-origin targets must bypass it. This mirrors Next.js Pages Router's
 * origin-based `isLocalURL` check: a same-origin absolute URL is still treated
 * as internal and navigated client-side. The parse is guarded so a malformed
 * relative target falls through to in-app navigation.
 */
const resolveExternalHref = (url: string | RouterUrlObject): string | null => {
  const raw = typeof url === 'string' ? url : url.pathname;
  try {
    const resolved = new URL(raw, window.location.origin);
    return resolved.origin === window.location.origin ? null : resolved.href;
  } catch {
    return null;
  }
};

/**
 * App-wide router hook, backed by React Router 7. Depend on this hook and the
 * `AppRouter` contract rather than `react-router-dom` directly.
 *
 * Contract notes (preserving Next.js Pages Router semantics for existing call
 * sites):
 * - `pathname` is the resolved browser path with the app basename removed and
 *   any trailing slash stripped, so `pathname === paths.*` comparisons hold in
 *   both dev (no trailing slash) and prod (`trailingSlash: true`).
 * - `asPath` is the full browser path including search and hash, with the
 *   trailing slash preserved (matching prod), since slug-parsing helpers were
 *   written against that shape.
 * - `query` merges RR7 route params and search params (see `mergeRouterQuery`).
 */
export const useAppRouter = (): AppRouter => {
  const location = useLocation();
  const params = useParams();
  const navigate = useNavigate();

  /**
   * Navigates to `url`. Cross-origin targets trigger a full-page navigation
   * (`window.location.assign`, preserving a history entry) instead of an in-app
   * `navigate`, matching the prior Next.js Pages Router behavior and `AppLink`.
   */
  const push = useCallback<AppRouter['push']>(
    async (url, options) => {
      const externalHref = resolveExternalHref(url);
      if (externalHref) {
        unblockRoutingChange();
        window.location.assign(externalHref);
        return true;
      }
      navigate(toNavigateTarget(url), {
        preventScrollReset: options?.scroll === false,
      });
      return true;
    },
    [navigate],
  );

  /**
   * Like `push`, but cross-origin targets use `window.location.replace` (no new
   * history entry) and same-origin navigations replace the current entry.
   */
  const replace = useCallback<AppRouter['replace']>(
    async (url, options) => {
      const externalHref = resolveExternalHref(url);
      if (externalHref) {
        unblockRoutingChange();
        window.location.replace(externalHref);
        return true;
      }
      navigate(toNavigateTarget(url), {
        replace: true,
        preventScrollReset: options?.scroll === false,
      });
      return true;
    },
    [navigate],
  );

  const back = useCallback<AppRouter['back']>(() => {
    navigate(-1);
  }, [navigate]);

  const pathname = stripTrailingSlash(location.pathname);
  const asPath = `${location.pathname}${location.search}${location.hash}`;
  const query = useMemo(
    () => mergeRouterQuery(params, new URLSearchParams(location.search)),
    [params, location.search],
  );

  return useMemo(
    () => ({
      pathname,
      asPath,
      query,
      isReady: true,
      push,
      replace,
      back,
    }),
    [pathname, asPath, query, push, replace, back],
  );
};
